import { TZDate } from '@date-fns/tz';

/**
 * Time-zone rules (SCH-02): every instant is stored in UTC, every person has an IANA
 * zone, and no time is ever shown without naming its zone.
 */

const cache = new Map<string, Intl.DateTimeFormat>();
function dtf(tz: string, opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${tz}|${JSON.stringify(opts)}`;
  let f = cache.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', { ...opts, timeZone: tz });
    cache.set(key, f);
  }
  return f;
}

export function isValidTimeZone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/** "America/New_York" → "New York". */
export function zoneCity(tz: string): string {
  return (tz.split('/').pop() ?? tz).replaceAll('_', ' ');
}

/** "Eastern Time (New York)". */
export function zoneLabel(tz: string, at: Date = new Date()): string {
  const generic = zonePart(tz, at, 'longGeneric');
  return tz === 'UTC' ? 'Coordinated Universal Time' : `${generic} (${zoneCity(tz)})`;
}

/** "Eastern Time" with no city. */
export function zoneName(tz: string, at: Date = new Date()): string {
  return zonePart(tz, at, 'longGeneric');
}

/** "ET", "CT", "PT". */
export function zoneAbbr(tz: string, at: Date = new Date()): string {
  return zonePart(tz, at, 'shortGeneric');
}

function zonePart(tz: string, at: Date, style: 'longGeneric' | 'shortGeneric'): string {
  const part = dtf(tz, { timeZoneName: style })
    .formatToParts(at)
    .find((p) => p.type === 'timeZoneName');
  return part?.value ?? tz;
}

/** A wall-clock date and time in `tz` as a UTC instant. `date` is YYYY-MM-DD, `time` is HH:mm. */
export function wallToUtc(date: string, time: string, tz: string): Date {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const [hh, mm] = time.split(':').map(Number) as [number, number];
  return new Date(new TZDate(y, m - 1, d, hh, mm, 0, tz).getTime());
}

/** The wall-clock date (YYYY-MM-DD) and time (HH:mm) of an instant in `tz`. */
export function utcToWall(instant: Date, tz: string): { date: string; time: string } {
  const parts = dtf(tz, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(instant);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '00';
  return { date: `${get('year')}-${get('month')}-${get('day')}`, time: `${get('hour')}:${get('minute')}` };
}

/** "Wednesday, August 12". */
export const fmtDayLong = (at: Date, tz: string) =>
  dtf(tz, { weekday: 'long', month: 'long', day: 'numeric' }).format(at);
/** "Wednesday, August 12, 2026". */
export const fmtDayLongYear = (at: Date, tz: string) =>
  dtf(tz, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }).format(at);
/** "Wed, Aug 12". */
export const fmtDayShort = (at: Date, tz: string) =>
  dtf(tz, { weekday: 'short', month: 'short', day: 'numeric' }).format(at);
/** "Aug 12". */
export const fmtMonthDay = (at: Date, tz: string) => dtf(tz, { month: 'short', day: 'numeric' }).format(at);
/** "Aug 12, 2026". */
export const fmtMonthDayYear = (at: Date, tz: string) =>
  dtf(tz, { month: 'short', day: 'numeric', year: 'numeric' }).format(at);
/** "2:00 PM". */
export const fmtTime = (at: Date, tz: string) => dtf(tz, { hour: 'numeric', minute: '2-digit' }).format(at);
/** "2:00 PM ET". */
export const fmtTimeZ = (at: Date, tz: string) => `${fmtTime(at, tz)} ${zoneAbbr(tz, at)}`;
/** "Wednesday, August 12, 2026 · 2:00 PM ET". */
export const fmtFull = (at: Date, tz: string) => `${fmtDayLongYear(at, tz)} · ${fmtTimeZ(at, tz)}`;

export interface AvailabilityDay {
  date: string;
  label: string;
  sub: 'Tomorrow' | 'Weekend' | null;
  weekend: boolean;
}

/** The days offered on the availability picker: tomorrow onward for `horizonDays` (SCH-01). */
export function availabilityDays(now: Date, tz: string, horizonDays: number): AvailabilityDay[] {
  const today = utcToWall(now, tz).date;
  const [y, m, d] = today.split('-').map(Number) as [number, number, number];
  const out: AvailabilityDay[] = [];
  for (let i = 1; i <= horizonDays; i++) {
    const noon = new Date(Date.UTC(y, m - 1, d + i, 12));
    const date = noon.toISOString().slice(0, 10);
    const weekday = noon.getUTCDay();
    const weekend = weekday === 0 || weekday === 6;
    out.push({
      date,
      label: dtf('UTC', { weekday: 'short', month: 'short', day: 'numeric' }).format(noon),
      sub: i === 1 ? 'Tomorrow' : weekend ? 'Weekend' : null,
      weekend,
    });
  }
  return out;
}

export interface DaySlot {
  time: string;
  label: string;
  section: 'Morning' | 'Afternoon' | 'Evening';
}

/** 30-minute starts from 8:00 AM to 7:30 PM, grouped as on the prototype picker. */
export const DAY_SLOTS: readonly DaySlot[] = (() => {
  const out: DaySlot[] = [];
  for (let minutes = 8 * 60; minutes <= 19 * 60 + 30; minutes += 30) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    const h12 = h % 12 === 0 ? 12 : h % 12;
    out.push({
      time: `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`,
      label: `${h12}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`,
      section: h < 12 ? 'Morning' : h < 18 ? 'Afternoon' : 'Evening',
    });
  }
  return out;
})();

/** Groups instants by their wall-clock day in `tz`, keeping order. */
export function groupByDay<T>(
  items: readonly T[],
  at: (item: T) => Date,
  tz: string,
): { day: string; items: T[] }[] {
  const out: { day: string; items: T[] }[] = [];
  for (const item of [...items].sort((a, b) => at(a).getTime() - at(b).getTime())) {
    const day = utcToWall(at(item), tz).date;
    const last = out.at(-1);
    if (last && last.day === day) last.items.push(item);
    else out.push({ day, items: [item] });
  }
  return out;
}

/** "4d", "3h", "12m": the age column on the case list. */
export function ageLabel(since: Date, now: Date): string {
  const minutes = Math.max(0, Math.floor((now.getTime() - since.getTime()) / 60_000));
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

/** Whole years between a date of birth (YYYY-MM-DD) and `now`. */
export function ageFromDob(dob: string, now: Date): number {
  const [y, m, d] = dob.split('-').map(Number) as [number, number, number];
  let age = now.getUTCFullYear() - y;
  const beforeBirthday = now.getUTCMonth() + 1 < m || (now.getUTCMonth() + 1 === m && now.getUTCDate() < d);
  if (beforeBirthday) age -= 1;
  return age;
}
